import { useStyletron } from '@thanx/uikit/theme'
import { Toggle } from '@thanx/uikit/toggle'
import phoneMockup from 'assets/images/order-feedback/written-feedback-phone-mockup.png'
import Information from 'components/Icons/Information'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import Section from 'components/Section'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { WRITTEN_FEEDBACK_TOGGLE_ID } from './hooks/useFocusWrittenFeedbackToggle'

const t = buildTranslate('order_ratings.settings.feedback_collection')

const PERMISSION_TOOLTIP_ID = 'written-feedback-toggle-permission'

// Measured off prototype v52's own computed styles: the row is TOP aligned with
// a 24px gap, the label is 15/22.5 bold, and the note is its own amber block
// 10px under the label, not a full-width bar across the card.
const ROW_GAP = '24px'
const LABEL_FONT_SIZE = '15px'
const LABEL_LINE_HEIGHT = '22.5px'
const LABEL_COLOR = palette.grey90
const NOTE_GAP_FROM_LABEL = '10px'
const NOTE_BACKGROUND = palette.eggYolk10
const NOTE_COLOR = palette.eggYolk90
const NOTE_FONT_SIZE = '12.5px'
const NOTE_LINE_HEIGHT = '17.5px'
const NOTE_RADIUS = '6px'
const NOTE_PADDING_VERTICAL = '8px'
const NOTE_PADDING_HORIZONTAL = '12px'
const NOTE_ICON_GAP = '7px'
const NOTE_ICON_SIZE = 16

type Props = {
  checked: boolean
  disabled: boolean
  // Driven by the SAVED setting, not `checked`. The note reports what guests
  // are getting right now, so an unpublished toggle must not raise or clear it.
  showOffNote: boolean
  // FR-60's hover/focus reason, defined ONLY while the viewer lacks
  // `manage MerchantSettings`. Kept separate from `disabled`, which also folds
  // in an in-flight save — feeding the combined flag to PermissionTooltip would
  // explain a save in progress as a permissions problem.
  permissionReason?: string
  onChange: (checked: boolean) => void
}

// The one control the Feedback collection sub-tab exists for (FR-23): whether a
// thumbs-down is followed by a request for a written comment.
//
// Presentational and fully controlled — the pane owns the slice, the initialize
// hook and the submit hook. No data fetching here.
//
// The phone mockup of the guest-facing written-feedback form, lifted from
// prototype v52 where it is a base64 PNG inside the single index.html. 269x400,
// rendered at 142px wide inside a 162x138 clipped box with the prototype's own
// 24px top corner radius, so it reads as the top of a phone screen. Decorative:
// the label beside it carries the meaning, so alt is empty.
const WrittenFeedbackCard = ({
  checked,
  disabled,
  showOffNote,
  permissionReason,
  onChange,
}: Props) => {
  const [css] = useStyletron()
  const label = t('written_feedback.label')

  return (
    <Section className="p-m mb-m">
      {/* Top aligned, not centred: v52 puts the toggle on the label's line and
          lets the note grow downward underneath it. */}
      <div
        className={css({
          display: 'flex',
          alignItems: 'flex-start',
          gap: ROW_GAP,
        })}
      >
        {/* Three nested boxes, matching prototype v52's .illus-box > .ph > img
            exactly: a 162x138 rounded window with a hairline inset, holding a
            142px phone whose 6px dark bezel is a BORDER on three sides so the
            device runs off the bottom edge, holding the 130px screenshot. Built
            from the prototype's own computed styles, not eyeballed. */}
        <div
          className="flex-shrink-0 d-flex align-items-end justify-content-center"
          style={{
            width: '162px',
            height: '138px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: 'rgba(20, 28, 38, 0.05) 0 0 0 1px inset',
          }}
        >
          <div
            style={{
              width: '142px',
              height: '122px',
              borderTopWidth: '6px',
              borderLeftWidth: '6px',
              borderRightWidth: '6px',
              borderBottomWidth: '0',
              borderStyle: 'solid',
              borderColor: '#232C37',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              overflow: 'hidden',
              boxShadow: 'rgba(20, 28, 38, 0.28) 0 10px 24px 0',
            }}
          >
            <img src={phoneMockup} alt="" style={{ width: '130px' }} />
          </div>
        </div>
        {/* Label and note share the middle column, so the note starts at the
            label's left edge and stops before the toggle rather than running
            the full width of the card under everything. */}
        <div className={css({ flex: '1 1 0%' })}>
          <div
            className={css({
              fontSize: LABEL_FONT_SIZE,
              lineHeight: LABEL_LINE_HEIGHT,
              fontWeight: 700,
              color: LABEL_COLOR,
            })}
          >
            {label}
          </div>
          {showOffNote && (
            <div
              className={css({
                display: 'flex',
                alignItems: 'flex-start',
                gap: NOTE_ICON_GAP,
                marginTop: NOTE_GAP_FROM_LABEL,
                backgroundColor: NOTE_BACKGROUND,
                color: NOTE_COLOR,
                fontSize: NOTE_FONT_SIZE,
                lineHeight: NOTE_LINE_HEIGHT,
                fontWeight: 600,
                borderRadius: NOTE_RADIUS,
                paddingTop: NOTE_PADDING_VERTICAL,
                paddingBottom: NOTE_PADDING_VERTICAL,
                paddingLeft: NOTE_PADDING_HORIZONTAL,
                paddingRight: NOTE_PADDING_HORIZONTAL,
              })}
            >
              {/* Decorative: the sentence beside it carries the meaning. */}
              <span aria-hidden="true" className={css({ display: 'flex' })}>
                <Information
                  type="info"
                  fillColor={NOTE_COLOR}
                  size={NOTE_ICON_SIZE}
                />
              </span>
              {t('written_feedback.off_note')}
            </div>
          )}
        </div>
        {/* `srOnlyReason` so the reason reaches assistive tech through
            aria-describedby instead of REPLACING the toggle's own accessible
            name — a screen-reader user must still be told which control is
            locked (WCAG 2.1 4.1.2). `inline` keeps the wrapper from forcing a
            new line inside the flex row. */}
        <PermissionTooltip
          disabled={Boolean(permissionReason)}
          reason={permissionReason}
          id={PERMISSION_TOOLTIP_ID}
          placement="left"
          inline
          srOnlyReason
        >
          <Toggle
            ariaLabel={label}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            // FR-58's gates focus this toggle by id
            // (useFocusWrittenFeedbackToggle) rather than a ref, because the
            // gate that needs to reach it can live in a different pane and
            // therefore a different component subtree.
            overrides={{
              Input: { props: { id: WRITTEN_FEEDBACK_TOGGLE_ID } },
            }}
          />
        </PermissionTooltip>
      </div>
    </Section>
  )
}

export default WrittenFeedbackCard
